import { Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import ServicePageLayout from '@/components/base/ServicePageLayout';

const breadcrumb = [
  { label: 'Accueil', to: '/' },
  { label: 'Dépannage menuiseries Béziers' },
];

const jsonLdService = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Dépannage et réparation de menuiseries à Béziers',
  provider: { '@id': 'https://heraultroulettesbaiesvitrees.fr/#business' },
  areaServed: { '@type': 'City', name: 'Béziers' },
  description: 'Réparation de baies coulissantes, volets roulants, vitrages, stores et fenêtres à Béziers. Intervention 24-48h, 7j/7. Devis gratuit, garantie 10 ans.',
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'EUR', description: 'Devis gratuit' },
};

const jsonLdFAQ = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Intervenez-vous sur les menuiseries anciennes du centre de Béziers ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Oui, nos techniciens sont habitués aux contraintes du bâti ancien biterrois. Nous savons intervenir sur les fenêtres bois et les baies aux dimensions atypiques des immeubles de caractère. Chaque réparation est réalisée avec soin pour préserver l\'intégrité de la menuiserie.',
      },
    },
    {
      '@type': 'Question',
      name: 'Réparez-vous les volets roulants électriques sur Béziers ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Absolument. Nous réparons tous types de volets roulants à Béziers : électriques filaires, radio, solaires et manuels. Diagnostic, changement de moteur ou de sangle, motorisation : nous intervenons sous 24 à 48h. Devis offert avant toute intervention.',
      },
    },
  ],
};

export default function BeziersPage() {
  return (
    <>
      <SEO
        title="Réparation baie coulissante Béziers | DFPRO"
        description="Baie coulissante qui coince, volet roulant bloqué ou vitre cassée à Béziers ? DFPRO intervient 24-48h, 7j/7 dans le Biterrois. Devis gratuit, garantie 10 ans."
        canonicalUrl="https://heraultroulettesbaiesvitrees.fr/depannage-menuiserie-beziers/"
      />
      <ServicePageLayout
        heroImage="/images/ville-herault-01.jpg"
        title="Réparation de menuiseries et baie coulissante à Béziers"
        subtitle="Volets roulants, baies coulissantes, vitrages : nos techniciens se déplacent à Béziers et dans tout le Biterrois. Devis gratuit, intervention 24-48h, 7j/7."
        breadcrumb={breadcrumb}
        jsonLdService={jsonLdService}
        jsonLdFAQ={jsonLdFAQ}
        ctaTitle="Une panne de menuiserie à Béziers ?"
        ctaText="Devis gratuit, intervention sous 24 à 48h, garantie 10 ans. Nos techniciens se déplacent dans tout le Biterrois, 7j/7."
        ctaServiceLabel="Tous nos services"
      >
        <p className="text-lg text-secondary-500 leading-relaxed">
          À Béziers, les menuiseries subissent chaleur sèche en été et vents violents toute l'année. Nous intervenons pour tout type de panne : baie coulissante qui coince, volet roulant bloqué, vitre brisée, fenêtre qui ne ferme plus. Diagnostic gratuit au <a href="tel:+33980802242" className="text-accent-500 font-semibold hover:underline">09 80 80 22 42</a>.
        </p>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/beziers-baie-01.jpg"
              alt="Réparation de baie vitrée coulissante à Béziers"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Baie coulissante : roulettes et rail à changer</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              De nombreuses constructions récentes à Béziers intègrent des baies coulissantes aluminium. Avec le temps, les roulettes s'encrassent et le rail peut se déformer sous la chaleur. Une baie qui <Link to="/baie-coulissante-qui-coince-herault/" className="text-accent-500 font-semibold hover:underline">coince ou déraille</Link> doit être réparée sans tarder.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              Nous proposons le <Link to="/changer-roulettes-galets-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">changement de roulettes</Link>, la <Link to="/reparation-rail-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">réparation de rail</Link>, le <Link to="/reglage-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">réglage</Link> et le <Link to="/remplacement-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">remplacement complet</Link>. Toutes marques : Technal, Sepalumic, Schüco.
            </p>
            <Link to="/reparation-baie-coulissante-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Voir le dépannage baie coulissante <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Volet roulant en panne : la panne la plus fréquente</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              Dans le Biterrois, les <Link to="/reparation-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">volets roulants</Link> sont en première ligne face au vent. Lame sortie de son axe après une rafale, <Link to="/reparation-moteur-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">moteur</Link> qui lâche sans prévenir, <Link to="/changer-sangle-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">sangle</Link> usée : nous intervenons sous 24h.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              Nous assurons aussi la <Link to="/motorisation-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">motorisation</Link> des volets manuels encore très présents dans les maisons de ville du centre de Béziers. Devis gratuit, garantie 10 ans.
            </p>
            <Link to="/reparation-volet-roulant-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Voir le dépannage volet roulant <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/beziers-volet-02.jpg"
              alt="Réparation de volet roulant à Béziers"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        </div>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/beziers-vitrage-03.avif"
              alt="Remplacement de vitrage à Béziers"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Vitrier et réparation fenêtre à Béziers</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              Carreau brisé dans une porte-fenêtre du centre ancien ou <Link to="/double-vitrage-embue-herault/" className="text-accent-500 font-semibold hover:underline">double vitrage embué</Link> dans une villa récente : notre service <Link to="/vitrier-herault/" className="text-accent-500 font-semibold hover:underline">vitrier</Link> intervient rapidement avec du double vitrage haute isolation.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              Pour les <Link to="/reparation-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">fenêtres</Link>, nous changeons <Link to="/changer-cremone-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">crémones</Link>, <Link to="/changer-joint-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">joints</Link> et assurons le <Link to="/reglage-oscillo-battant-herault/" className="text-accent-500 font-semibold hover:underline">réglage oscillo-battant</Link>. Tous matériaux : PVC, alu, bois.
            </p>
            <Link to="/vitrier-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Voir le service vitrier <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        <h2>Intervention à Béziers sous 24 à 48h</h2>
        <p>
          Nous couvrons Béziers intra-muros et tous ses quartiers : centre historique, allées Paul Riquet, zone du Polygone, quartier de la gare, la Devèze et secteurs périphériques. Également Vendres, Sauvian, Sérignan, Boujan-sur-Libron. Consultez aussi <Link to="/depannage-menuiserie-agde/" className="text-accent-500 font-semibold hover:underline">Agde</Link> et <Link to="/depannage-menuiserie-pezenas/" className="text-accent-500 font-semibold hover:underline">Pézenas</Link>. Retrouvez notre <Link to="/" className="text-accent-500 font-semibold hover:underline">page d'accueil</Link>.
        </p>
      </ServicePageLayout>
    </>
  );
}